import {
  ArrowLeft,
  ChevronDown,
  Clock3,
  Inbox,
  PanelLeftClose,
  PanelLeftOpen,
  PhoneCall,
  Plus,
  Search,
  SquareUserRound,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';

import { useWhatsAppInbox } from './use-whatsapp';
import { WhatsAppCallHistory } from './WhatsAppCallHistory';
import {
  ChatView,
  NewChatView,
  NotOnWhatsAppBadge,
  StatusTick,
  type ChatTarget,
} from './WhatsAppWidget';

import type { WhatsAppInboxItem } from './whatsapp.api';
import type { LeadPhoneTarget } from '@/features/leads/leads.api';

import { useCurrentUser } from '@/features/auth/use-auth';

/**
 * The floating widget's panel, laid out like DoubleTick's web app: a filter
 * rail, the chat list, the open chat and a details column. The chat itself
 * is ChatView (page variant). Which chat is open is owned by the widget
 * (`target`), so the widget bus - lead pages, notification taps - can point
 * it at a thread. Filters here are purely client-side views over the inbox.
 */

/**
 * Rail = scope x state, like DoubleTick: every scope expands to the same
 * states. "My chats" is chats on leads assigned to the signed-in seller.
 */
type Scope = 'all' | 'mine';
type State = 'awaiting' | 'unread' | 'all';

const SCOPE_LABEL: Record<Scope, string> = {
  all: 'All chats',
  mine: 'My chats',
};
const STATE_LABEL: Record<State, string> = {
  awaiting: 'Awaiting reply',
  unread: 'Unread',
  all: 'All',
};
const STATES: readonly State[] = ['awaiting', 'unread', 'all'];

const inScope = (c: WhatsAppInboxItem, scope: Scope, myUserId: string | null): boolean => {
  switch (scope) {
    case 'mine':
      return Boolean(myUserId) && c.lead?.assignedAgentId === myUserId;
    default:
      return true;
  }
};

/** Awaiting = the customer spoke last. */
const inState = (c: WhatsAppInboxItem, state: State): boolean => {
  switch (state) {
    case 'awaiting':
      return c.lastMessage?.direction === 'IN';
    case 'unread':
      return c.unreadCount > 0;
    default:
      return true;
  }
};

const nameOf = (c: WhatsAppInboxItem): string =>
  c.kind === 'GROUP'
    ? (c.contactName ?? 'WhatsApp group')
    : (c.lead?.customerName ?? c.contactName ?? `+${c.phone}`);

const initialsOf = (name: string | null, phone: string): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return phone.slice(-2);
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
};

/** Chat-list timestamp: time today, weekday this week, else a short date. */
const listTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  if (now.getTime() - d.getTime() < 6 * 86_400_000) {
    return d.toLocaleDateString('en-IN', { weekday: 'short' });
  }
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const AVATAR_TONES = [
  'bg-violet-100 text-violet-700',
  'bg-sky-100 text-sky-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-emerald-100 text-emerald-700',
  'bg-indigo-100 text-indigo-700',
];
const toneFor = (key: string): string => {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length]!;
};

const Avatar = ({
  name,
  phone,
  size = 'md',
  group = false,
}: {
  name: string | null;
  phone: string;
  size?: 'md' | 'lg';
  /** A per-lead WhatsApp group: people icon instead of initials. */
  group?: boolean;
}): React.JSX.Element => (
  <span className="relative shrink-0">
    <span
      className={`flex items-center justify-center rounded-full font-semibold ${
        group ? 'bg-emerald-100 text-emerald-800' : toneFor(phone)
      } ${size === 'lg' ? 'h-12 w-12 text-base' : 'h-10 w-10 text-[13px]'}`}
    >
      {group ? (
        <Users className={size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'} />
      ) : (
        initialsOf(name, phone)
      )}
    </span>
    <span
      className={`absolute -right-0.5 -bottom-0.5 flex items-center justify-center rounded-full bg-white ${
        size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'
      }`}
    >
      <FaWhatsapp className={`text-[#25D366] ${size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'}`} />
    </span>
  </span>
);

export const WhatsAppInbox = ({
  target,
  onTargetChange,
  onClose,
}: {
  target: ChatTarget | null;
  onTargetChange: (target: ChatTarget | null) => void;
  /** Absent on the page, where the browser's own navigation closes it. */
  onClose?: () => void;
}): React.JSX.Element => {
  const inbox = useWhatsAppInbox(true);
  const me = useCurrentUser();
  const myUserId = me?.id ?? null;
  // Lead id + owner on each chat row: admin and sales lead only (the API
  // sends the owner's name to those two roles alone).
  const showLeadOwner = me?.role === 'ADMIN' || me?.role === 'SALES_LEAD';
  const items = useMemo(() => inbox.data?.conversations ?? [], [inbox.data]);

  const [scope, setScope] = useState<Scope>('all');
  const [state, setState] = useState<State>('all');
  // Which scopes are expanded in the rail (DoubleTick opens "My chats").
  const [expanded, setExpanded] = useState<Record<Scope, boolean>>({
    all: false,
    mine: true,
  });
  const [railOpen, setRailOpen] = useState(true);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [newChat, setNewChat] = useState(false);

  /** Per scope: total, plus a count per state (0 is not shown). */
  const counts = useMemo(() => {
    const out = {} as Record<Scope, Record<State, number>>;
    for (const sc of ['all', 'mine'] as const) {
      const scoped = items.filter((c) => inScope(c, sc, myUserId));
      out[sc] = {
        awaiting: scoped.filter((c) => inState(c, 'awaiting')).length,
        unread: scoped.filter((c) => inState(c, 'unread')).length,
        all: scoped.length,
      };
    }
    return out;
  }, [items, myUserId]);

  const q = query.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  const visible = items
    .filter((c) => inScope(c, scope, myUserId) && inState(c, state))
    .filter((c) => {
      if (!q) return true;
      const hay = [c.lead?.customerName, c.contactName, c.lead?.reference, c.lead?.destination]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q) || (digits.length > 2 && c.phone.includes(digits));
    });

  const openConversation = (id: string): void => {
    setNewChat(false);
    onTargetChange({ kind: 'convo', id });
  };

  const openLead = (
    leadId: string,
    name: string,
    destination?: string,
    phoneTarget?: LeadPhoneTarget,
  ): void => {
    setNewChat(false);
    onTargetChange({ kind: 'lead', id: leadId, name, destination, phoneTarget });
  };

  const switchNumber = (phoneTarget: LeadPhoneTarget): void => {
    if (target?.kind !== 'lead') return;
    onTargetChange({ ...target, phoneTarget });
  };

  const countChip = (n: number, active: boolean): React.JSX.Element | null =>
    n > 0 ? (
      <span
        className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
          active ? 'bg-emerald-600 text-white' : 'bg-zinc-200 text-zinc-700'
        }`}
      >
        {n}
      </span>
    ) : null;

  /** One scope group: a collapsible header and the five state rows under it. */
  const railGroup = (sc: Scope, icon: React.ReactNode): React.JSX.Element => {
    const open = expanded[sc];
    const groupActive = scope === sc;
    return (
      <div key={sc}>
        <button
          type="button"
          onClick={() => {
            // Collapsed group: expand it and select it. Expanded: toggle.
            if (!open) {
              setExpanded((e) => ({ ...e, [sc]: true }));
              setScope(sc);
              setState('all');
            } else {
              setExpanded((e) => ({ ...e, [sc]: false }));
            }
          }}
          className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-left text-[13.5px] transition-colors ${
            groupActive && !open
              ? 'bg-emerald-50 font-medium text-emerald-800'
              : 'text-zinc-700 hover:bg-zinc-100'
          }`}
        >
          {icon}
          <span className="flex-1 truncate">{SCOPE_LABEL[sc]}</span>
          {!open && countChip(counts[sc].all, groupActive)}
          <ChevronDown
            className={`h-4 w-4 text-zinc-400 transition-transform ${open ? '' : '-rotate-90'}`}
          />
        </button>
        {open && (
          <div className="space-y-0.5">
            {STATES.map((st) => {
              const active = scope === sc && state === st;
              return (
                <button
                  key={st}
                  type="button"
                  onClick={() => {
                    setScope(sc);
                    setState(st);
                  }}
                  className={`flex w-full items-center gap-2 rounded-lg py-1.5 pr-2 pl-9 text-left text-[13.5px] transition-colors ${
                    active
                      ? 'bg-emerald-50 font-medium text-emerald-800'
                      : 'text-zinc-700 hover:bg-zinc-100'
                  }`}
                >
                  <span className="flex-1 truncate">{STATE_LABEL[st]}</span>
                  {st === 'awaiting' && <Clock3 className="h-3.5 w-3.5 text-zinc-400" />}
                  {countChip(counts[sc][st], active)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      {/* ---- Filter rail ---- */}
      {railOpen && (
        <aside className="hidden w-52 shrink-0 flex-col border-r border-zinc-200 lg:flex">
          <div className="flex items-center justify-between px-4 py-3">
            <h1 className="text-[16px] font-semibold text-zinc-900">Inbox</h1>
            <div className="flex items-center gap-1 text-zinc-500">
              <button
                type="button"
                title="Collapse"
                onClick={() => setRailOpen(false)}
                className="rounded-md p-1.5 hover:bg-zinc-100"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
              {onClose && (
                <button
                  type="button"
                  title="Close"
                  aria-label="Close WhatsApp"
                  onClick={onClose}
                  className="rounded-md p-1.5 hover:bg-zinc-100"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto px-2">
            {railGroup('all', <Inbox className="h-4 w-4 text-zinc-500" />)}
            {railGroup('mine', <SquareUserRound className="h-4 w-4 text-zinc-500" />)}
          </nav>

          {/* Calls on the open chat - refreshed live by the call stream. */}
          {target && !newChat && (
            <div className="flex max-h-[45%] min-h-0 flex-col border-t border-zinc-200">
              <div className="flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold tracking-wide text-zinc-500 uppercase">
                <PhoneCall className="h-3.5 w-3.5" />
                Calls
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <WhatsAppCallHistory
                  {...(target.kind === 'lead'
                    ? { leadId: target.id }
                    : { conversationId: target.id })}
                />
              </div>
            </div>
          )}
        </aside>
      )}

      {/* ---- Chat list ---- */}
      <section
        className={`relative flex w-full shrink-0 flex-col border-r border-zinc-200 md:w-[280px] ${
          target || newChat ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="flex h-12 items-center gap-1.5 border-b border-zinc-200 px-3">
          {!railOpen && (
            <button
              type="button"
              title="Show filters"
              onClick={() => setRailOpen(true)}
              className="hidden rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 lg:block"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}
          {searchOpen ? (
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg bg-zinc-100 px-3 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-zinc-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchOpen(false);
                    setQuery('');
                  }
                }}
                placeholder="Search by name, phone, lead…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400"
              />
              <button
                type="button"
                aria-label="Close search"
                onClick={() => {
                  setSearchOpen(false);
                  setQuery('');
                }}
                className="rounded p-0.5 text-zinc-500 hover:bg-zinc-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <h2 className="flex-1 truncate text-[15px] font-semibold text-zinc-900">
                {SCOPE_LABEL[scope]}
                {state !== 'all' && (
                  <span className="font-normal text-zinc-500"> · {STATE_LABEL[state]}</span>
                )}
              </h2>
              <button
                type="button"
                title="Search chats"
                onClick={() => setSearchOpen(true)}
                className="rounded-md p-1.5 text-zinc-600 hover:bg-zinc-100"
              >
                <Search className="h-[18px] w-[18px]" />
              </button>
              {onClose && (
                <button
                  type="button"
                  title="Close"
                  aria-label="Close WhatsApp"
                  onClick={onClose}
                  className={`rounded-md p-1.5 text-zinc-600 hover:bg-zinc-100 ${railOpen ? 'lg:hidden' : ''}`}
                >
                  <X className="h-[18px] w-[18px]" />
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {inbox.isLoading ? (
            <p className="p-4 text-sm text-zinc-500">Loading chats…</p>
          ) : visible.length === 0 ? (
            <p className="p-4 text-sm text-zinc-500">
              {q ? `No chats match "${query.trim()}".` : 'No chats here.'}
            </p>
          ) : (
            visible.map((c) => {
              const active = target?.kind === 'convo' && target.id === c.id;
              const preview = c.lastMessage
                ? (c.lastMessage.body ?? `[${c.lastMessage.kind.toLowerCase()}]`)
                : '';
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openConversation(c.id)}
                  className={`flex w-full items-center gap-2.5 border-b border-zinc-100 px-3 py-2.5 text-left transition-colors ${
                    active ? 'bg-emerald-50/70' : 'hover:bg-zinc-50'
                  }`}
                >
                  <Avatar
                    name={c.lead?.customerName ?? c.contactName}
                    phone={c.phone}
                    group={c.kind === 'GROUP'}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[14px] font-medium text-zinc-900">
                          {nameOf(c)}
                        </span>
                        {c.notOnWhatsApp && <NotOnWhatsAppBadge compact />}
                      </span>
                      {c.unreadCount > 0 && (
                        <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-[#25D366] px-1.5 text-[11px] font-bold text-white">
                          {c.unreadCount > 99 ? '99+' : c.unreadCount}
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1 text-[12.5px] text-zinc-500">
                        {c.lastMessage?.direction === 'OUT' && (
                          <span className="shrink-0">
                            <StatusTick status={c.lastMessage.status} />
                          </span>
                        )}
                        <span className="truncate">{preview}</span>
                      </span>
                      <span className="shrink-0 text-[11.5px] text-zinc-400">
                        {listTime(c.lastMessageAt)}
                      </span>
                    </span>
                    {showLeadOwner && c.lead && (
                      <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[11.5px] text-zinc-500">
                        <span className="shrink-0 rounded bg-zinc-100 px-1.5 py-px font-medium text-zinc-600">
                          {c.lead.reference}
                        </span>
                        <UserRound className="h-3 w-3 shrink-0 text-zinc-400" />
                        <span
                          className={`truncate ${c.lead.assignedAgentName ? '' : 'text-amber-600'}`}
                        >
                          {c.lead.assignedAgentName ?? 'Unassigned'}
                        </span>
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setNewChat(true);
            onTargetChange(null);
          }}
          className="absolute right-3 bottom-3 flex items-center gap-1.5 rounded-xl bg-[#1DAA61] px-3.5 py-2 text-[13px] font-semibold text-white shadow-lg hover:bg-[#189a57]"
        >
          <Plus className="h-4 w-4" />
          New
        </button>
      </section>

      {/* ---- Conversation ---- */}
      <section
        className={`min-w-0 flex-1 flex-col bg-[#ECE5DD] ${target || newChat ? 'flex' : 'hidden md:flex'}`}
      >
        {newChat ? (
          <div className="flex h-full flex-col bg-white">
            <NewChatView onBack={() => setNewChat(false)} onPick={openLead} />
          </div>
        ) : target ? (
          <>
            {/* Phones: the list is hidden while a chat is open, so give it a way back. */}
            <div className="flex items-center border-b border-zinc-200 bg-white px-2 py-1.5 md:hidden">
              <button
                type="button"
                onClick={() => onTargetChange(null)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] text-zinc-700 hover:bg-zinc-100"
              >
                <ArrowLeft className="h-4 w-4" />
                All chats
              </button>
            </div>
            <ChatView
              key={`${target.kind}:${target.id}:${target.kind === 'lead' ? (target.phoneTarget ?? 'PRIMARY') : ''}`}
              target={target}
              onSwitchNumber={switchNumber}
              variant="page"
            />
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-sm">
              <FaWhatsapp className="h-10 w-10 text-[#25D366]" />
            </span>
            <p className="text-[15px] font-medium text-zinc-700">Pick a chat to start</p>
            <p className="max-w-xs text-[13px] text-zinc-500">
              Conversations with your leads show on the left. Use New to message a lead who has not
              written in yet.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};
